import { rect, px, mRect, mPx } from '../../../pixel/canvas';
import type { Part, Anchor, Palette } from '../../types';

// Anchor = upper back. Back attachments paint OUT FROM the anchor (a.x ± offset).
// Wings extend ~6-8 px to each side; spikes/shells stay close to the spine.

const drawBatWingsDown = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Membrane main span.
  mRect(ctx, 4, a.y, 6, 2, p.shadow);
  // Outer rim along the bottom edge.
  mRect(ctx, 4, a.y + 1, 6, 1, p.dark);
  // Wing-bone highlight along the top edge.
  mRect(ctx, 4, a.y, 6, 1, p.metalDark);
};

const drawBatWingsUp = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Membrane main span (raised).
  mRect(ctx, 4, a.y - 3, 6, 2, p.shadow);
  // Outer rim along the bottom edge.
  mRect(ctx, 4, a.y - 2, 6, 1, p.dark);
  // Wing-bone highlight along the top edge.
  mRect(ctx, 4, a.y - 3, 6, 1, p.metalDark);
};

const drawFeatheredWingsDown = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Feather base.
  mRect(ctx, 4, a.y, 6, 2, p.light);
  // Feather tips along the bottom edge.
  mRect(ctx, 4, a.y + 1, 6, 1, p.shadow);
  // Vertical "tooth" stripes for feather texture along the outer wing edge.
  mPx(ctx, 9, a.y, p.shadow);
  mPx(ctx, 9, a.y + 2, p.shadow);
  mPx(ctx, 7, a.y + 2, p.shadow);
};

const drawFeatheredWingsUp = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Feather base (raised).
  mRect(ctx, 4, a.y - 3, 6, 2, p.light);
  // Feather tips along the bottom edge.
  mRect(ctx, 4, a.y - 2, 6, 1, p.shadow);
  // Vertical "tooth" stripes for feather texture along the outer wing edge.
  mPx(ctx, 9, a.y - 3, p.shadow);
  mPx(ctx, 9, a.y - 1, p.shadow);
  mPx(ctx, 7, a.y - 1, p.shadow);
};

export const back: Record<string, Part> = {
  bat_wings: {
    id: 'bat_wings',
    category: 'back',
    name: 'Bat Wings',
    requires: 'back',
    weights: { agility: 10, stealth: 8, arcane: 4 },
    draw: drawBatWingsDown,
    frames: [
      { draw: drawBatWingsDown },
      { draw: drawBatWingsUp },
    ],
  },
  feathered_wings: {
    id: 'feathered_wings',
    category: 'back',
    name: 'Feathered Wings',
    requires: 'back',
    weights: { agility: 14, arcane: 6 },
    draw: drawFeatheredWingsDown,
    frames: [
      { draw: drawFeatheredWingsDown },
      { draw: drawFeatheredWingsUp },
    ],
  },
  spikes: {
    id: 'spikes',
    category: 'back',
    name: 'Spikes',
    requires: 'back',
    weights: { ferocity: 14, resilience: 4 },
    draw: (ctx, a, p) => {
      // Center spike (tallest).
      rect(ctx, a.x - 1, a.y - 4, 2, 4, p.metalDark);
      px(ctx, a.x, a.y - 5, p.metalLight);
      // Inner pair of spikes.
      mRect(ctx, 3, a.y - 3, 1, 3, p.metalDark);
      mPx(ctx, 3, a.y - 4, p.metalLight);
      // Outer pair of spikes (shortest).
      mRect(ctx, 5, a.y - 2, 1, 2, p.metalDark);
    },
  },
  shell: {
    id: 'shell',
    category: 'back',
    name: 'Shell',
    requires: 'back',
    weights: { resilience: 18, stealth: -4 },
    draw: (ctx, a, p) => {
      // Shadow under the dome for depth.
      rect(ctx, a.x - 4, a.y + 2, 8, 1, p.shadow);
      // Main dome body.
      rect(ctx, a.x - 4, a.y - 2, 8, 4, p.dark);
      rect(ctx, a.x - 3, a.y - 3, 6, 1, p.dark);
      rect(ctx, a.x - 2, a.y - 4, 4, 1, p.dark);
      // Rim band along the base of the dome.
      rect(ctx, a.x - 4, a.y - 2, 8, 1, p.metalDark);
      // Small highlight on the dome.
      px(ctx, a.x - 3, a.y - 3, p.metalLight);
    },
  },
};
